import {
  PRE_PROD_DOMAIN,
  PRE_PROD_DOMAIN_CERBERUS,
  PRE_PROD_DOMAIN_CERBERUS_2,
  PROD_DOMAIN,
  UAT_DOMAIN,
  MFP_ADOBE_CJA_SDK_PROD,
  MFP_ADOBE_CJA_SDK_PREPROD,
  MFP_ADOBE_CJA_SDK_DEV,
  LOCAL_DOMAIN
} from '../../app/src/utils/constants/common.js';
import { CJA_EVENTS, COMMON, ERROR_MAP } from './constants.js';

/**
 * The function `getSdkBasedOnEnvironment` determines the appropriate SDK URL based on the current
 * domain.
 * @returns The function `getSdkBasedOnEnvironment` returns the URL of the Adobe CJA SDK
 * based on the current domain of the application.
 * It checks the `window.location.origin` against predefined domains and
 * returns the corresponding SDK URL for production, pre-production,
 * or development environments. If the domain is unknown,
 * it logs a debug message and returns an empty string.
 */
function getSdkBasedOnEnvironment() {
  const currentDomain = window.location.origin;
  let sdkUrl = '';

  switch (currentDomain) {
    case PROD_DOMAIN:
      sdkUrl = MFP_ADOBE_CJA_SDK_PROD;
      break;
    case PRE_PROD_DOMAIN:
      sdkUrl = MFP_ADOBE_CJA_SDK_PREPROD;
      break;
    case UAT_DOMAIN:
    case PRE_PROD_DOMAIN_CERBERUS:
    case PRE_PROD_DOMAIN_CERBERUS_2:
      sdkUrl = MFP_ADOBE_CJA_SDK_DEV;
      break;
    default:
      console.error('[CJA Logs]: Unknown domain, no SDK URLs set');
      break;
  }
  return sdkUrl;
}

function updateUserInfoObjKey(key, value) {
  const userInfo = window?.pageInfoGlobal?.userInfo;
  if (!userInfo || !key) return;
  if (key === 'userRole') {
    if (!Array.isArray(value) || value.length === 0 || value.join(',').trim() === '') {
      userInfo[key] = ['foundryuser'];
    } else {
      userInfo[key] = value;
    }
  } else {
    userInfo[key] = value;
  }
  window.pageInfoGlobal.userInfo = JSON.parse(JSON.stringify(userInfo));
}

async function setUserInfoObj() {
  let userRole = JSON.parse(window.localStorage.getItem('roles'));
  if (!userRole || !Array.isArray(userRole) || userRole.join(',') === '') {
    userRole = ['foundryuser']; // This is discussed with team, default user role is foundryUser if no roles are found
  }
  const userInfoObj = {
    userRole: userRole.join(',') || window.pageInfoGlobal.userInfo?.userRole || '',
    userId: window.localStorage.getItem('userId') || window.pageInfoGlobal.userInfo?.userId || '',
    foundryId: window.localStorage.getItem('sessionFoundryId') || window.pageInfoGlobal.userInfo?.foundryId || '',
    foundryName: window.localStorage.getItem('sessionFoundryName') || window.pageInfoGlobal.userInfo?.foundryName || '',
    userStatus: window.localStorage.getItem('current_user') ? COMMON.LOGGED_USER : COMMON.GUEST,
  };
  console.debug('[CJA Logs]: User Info Object', userInfoObj);
  window.pageInfoGlobal.userInfo = JSON.parse(JSON.stringify(userInfoObj));
}

function setPageInfoObj(to) {
  if (window?.pageInfoGlobal?.pageInfo) {
    const routePath = processRoutePath(to?.matched[0]?.path);
    const pageInfo = {
      pageName: 'mfp:' + (routePath[1] || ''),
      pageSection: routePath[0] || '',
      pageUrl: window.location.href
    };
    window.pageInfoGlobal.pageInfo = JSON.parse(JSON.stringify(pageInfo));
    setPageErrorObj(to);
  }
}

function setPageErrorObj(to) {
  const errorObj = ERROR_MAP[to?.name] || null;
  if (errorObj) {
    window.pageInfoGlobal.pageError = JSON.parse(JSON.stringify(errorObj));
  } else {
    window.pageInfoGlobal.pageError = {
      errorCode: '',
      errorMessage: '',
      errorType: ''
    };
  }
}

/**
 * The function `routerCallbackToSendPageLoadEvent` updates the `pageInfoGlobal` object with page
 * information and dispatches a custom analytics event after a delay.
 * @param to - The `to` parameter in the `routerCallbackToSendPageLoadEvent` function is typically an
 * object that represents the route being navigated to in a web application. It may contain information
 * about the matched route path, such as `to.matched[0].path`, which is used to determine the
 */
function routerCallbackToSendPageLoadEvent(to) {
  setPageInfoObj(to);
  setUserInfoObj();

  setTimeout(() => {
    if (window?.CustomAnalyticsEventEmitter && window?.pageInfoGlobal) {
      adobeCjaEventEmitter(window.pageInfoGlobal);
    }
  }, COMMON.PAGE_LOAD_EVENT_DELAY);
}

/**
 * Helper function to sanitize strings by trimming and converting to lowercase
 * @param {string} value - The string value to sanitize
 * @returns {string} The sanitized string
 */
function sanitizeString(value) {
  return typeof value === 'string' ? value.toLowerCase().trim() : '';
}

/**
 * Recursively sanitizes all string values in an object
 * @param {object} obj - The object to sanitize
 * @returns {object} The sanitized object
 */
function sanitizeObject(obj) {
  if (!obj || typeof obj !== 'object') return {};

  const sanitized = { ...obj };
  for (const key in sanitized) {
    if (typeof sanitized[key] === 'string') {
      sanitized[key] = sanitizeString(sanitized[key]);
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeObject(sanitized[key]); // Recursively sanitize nested objects
    } else if (typeof sanitized[key] === 'number') {
      // Leave numbers as is
    } else if (sanitized[key] === null || sanitized[key] === undefined) {
      sanitized[key] = ''; // Convert null/undefined to empty string
    }
  }
  return sanitized;
}

/**
 * The function adobeCjaEventEmitter sends a custom event with Adobe Analytics payload if
 * CustomAnalyticsEventEmitter and pageInfoGlobal exist.
 * @param adobeAnalyticsPayload - It looks like you were about to provide some information about the
 * `adobeAnalyticsPayload` parameter, but it seems to be missing. Could you please provide the details
 * or let me know if you need any assistance with this function?
 */
function adobeCjaEventEmitter(adobeAnalyticsPayload) {
  if (window?.CustomAnalyticsEventEmitter && window?.pageInfoGlobal) {
    const clonedPayload = JSON.parse(JSON.stringify(adobeAnalyticsPayload));

    const originalEvent = clonedPayload.event;
    const originalEventInfo = clonedPayload.eventInfo;

    const sanitizedPayload = sanitizeObject(clonedPayload);

    if (originalEvent) sanitizedPayload.event = originalEvent;
    if (originalEventInfo) sanitizedPayload.eventInfo = originalEventInfo;

    CustomAnalyticsEventEmitter.dispatch(CJA_EVENTS.CUSTOM_EVENT, sanitizedPayload);
  } else {
    console.error('[CJA Logs]: Event not sent - missing CustomAnalyticsEventEmitter or pageInfoGlobal');
  }
}

/**
 * The function `processRoutePath` takes a route path as input, extracts the base path and flat path
 * segments, and returns them as an array.
 * @param routePath - The `processRoutePath` function takes a `routePath` parameter, which is a string
 * representing a route path. This route path may contain segments separated by slashes ("/") and may
 * include dynamic segments indicated by a colon (":"). The function processes this route path and
 * returns an array containing the base
 * @returns An array containing two elements is being returned. The first element is the `basePath`,
 * which is the first segment of the route path or an empty string if the route path is empty. The
 * second element is the `flatPath`, which is a modified version of the route path where each segment
 * is prefixed with a colon (:) except for the first segment.
 */
function processRoutePath(routePath) {
  if (!routePath) return ['', ''];
  const segments = routePath.split('/').filter(Boolean);
  const basePath = segments[0] || '';
  const flatPath = segments
    .map(seg => (seg.startsWith(':') ? seg : `:${seg}`))
    .join('')
    .slice(1);
  return [basePath, flatPath];
}

export {
  setUserInfoObj,
  setPageInfoObj,
  routerCallbackToSendPageLoadEvent,
  getSdkBasedOnEnvironment,
  adobeCjaEventEmitter,
  updateUserInfoObjKey
};
